import isEmpty from 'lodash/isEmpty'
import type { PivotExpandState, PivotNode, PivotResult, PivotRowItem } from './types'
import { isPivotExpanded } from './isPivotExpanded'
import { PivotRowKindEnum } from './PivotRowKindEnum'

/** Flattens the pivot row tree into renderable row items. */
export const listPivotRows = ({
  result,
  expandState,
  showSubtotals,
}: {
  result: PivotResult
  expandState: PivotExpandState
  showSubtotals: boolean
}): PivotRowItem[] => {
  const items: PivotRowItem[] = []
  const depth = result.rowFields.length
  const isExpanded = (key: string): boolean => isPivotExpanded(expandState, key)
  if (depth === 0) {
    items.push({ _id: result.rowRoot.key, kind: PivotRowKindEnum.Leaf, path: [], labelFromLevel: 0 })
    return items
  }
  const labelFromLevel = (path: PivotNode[], kind: PivotRowKindEnum): number => {
    const previous = isEmpty(items) ? undefined : items[items.length - 1].path
    if (!previous) return 0
    if (kind === PivotRowKindEnum.Subtotal) return path.length - 1
    const max = Math.max(path.length, previous.length)
    for (let level = 0; level < max; level++) {
      if (path[level]?.key !== previous[level]?.key) return level
    }
    return 0
  }
  const visit = (node: PivotNode, parentPath: PivotNode[]): void => {
    const path = [...parentPath, node]
    if (node.depth === depth) {
      items.push({
        _id: node.key,
        kind: PivotRowKindEnum.Leaf,
        path,
        labelFromLevel: labelFromLevel(path, PivotRowKindEnum.Leaf),
      })
      return
    }
    if (!isExpanded(node.key)) {
      items.push({
        _id: node.key,
        kind: PivotRowKindEnum.Group,
        path,
        labelFromLevel: labelFromLevel(path, PivotRowKindEnum.Group),
      })
      return
    }
    node.children.forEach((child) => visit(child, path))
    if (showSubtotals) {
      items.push({
        _id: `${node.key}:total`,
        kind: PivotRowKindEnum.Subtotal,
        path,
        labelFromLevel: labelFromLevel(path, PivotRowKindEnum.Subtotal),
      })
    }
  }
  result.rowRoot.children.forEach((child) => visit(child, []))
  return items
}
